import { Holding, Watchlist, WatchlistSymbol } from './entities';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { arraySymmetricDifference } from '@benzinga/utils';
import { StockSymbol } from '@benzinga/session';

interface WatchlistEvent {
  type: 'watchlist:created_watchlist' | 'watchlist:updated_watchlist' | 'watchlist:removed_watchlist';
  watchlist: Watchlist;
}
interface WatchlistTickerEvent {
  type: 'watchlist:added_ticker_to_watchlist' | 'watchlist:removed_ticker_from_watchlist';
  watchlist: Watchlist;
  symbols: StockSymbol[];
}
interface WatchlistUpdateEvent {
  type: 'watchlist:updated_watchlists';
  watchlists: Watchlist[];
}

export type WatchlistStoreEvent = WatchlistUpdateEvent | WatchlistEvent | WatchlistTickerEvent;

export class WatchlistStore extends ListenableSubscribable<WatchlistStoreEvent> {
  private date?: Date;
  private watchlists?: Watchlist[];

  constructor() {
    super();
    this.date = undefined;
    this.watchlists = undefined;
  }

  public static compareWatchlists = (lhs: Watchlist[], rhs: Watchlist[]): boolean => {
    if (
      arraySymmetricDifference(
        lhs.map(w => w.watchlistId),
        rhs.map(w => w.watchlistId),
      ).length === 0
    ) {
      return lhs?.every(watchlist => {
        const newWatchlist = rhs.find(newWatchlist => watchlist.watchlistId === newWatchlist.watchlistId);
        if (newWatchlist) {
          return (
            arraySymmetricDifference(
              watchlist.symbols.map(s => s.symbolId),
              newWatchlist.symbols.map(s => s.symbolId),
            ).length === 0 &&
            watchlist.symbols.every(symbol => {
              const newSymbol = newWatchlist.symbols.find(s => s.symbolId === symbol.symbolId);
              return newSymbol ? WatchlistStore.compareHoldings(symbol.holding, newSymbol.holding) : false;
            })
          );
        } else {
          return false;
        }
      });
    }
    return false;
  };

  // Compares the value fields a brokerage sync can change; deliberately ignores
  // timestamp fields so a refetch with unchanged positions still counts as equal.
  private static compareHoldings = (
    lhs: Holding | Holding[] | undefined,
    rhs: Holding | Holding[] | undefined,
  ): boolean => {
    const lhsHoldings = Array.isArray(lhs) ? lhs : lhs ? [lhs] : [];
    const rhsHoldings = Array.isArray(rhs) ? rhs : rhs ? [rhs] : [];
    if (lhsHoldings.length !== rhsHoldings.length) {
      return false;
    }
    return lhsHoldings.every((holding, index) => {
      const newHolding = rhsHoldings[index];
      return (
        holding.quantity === newHolding.quantity &&
        holding.costBasis === newHolding.costBasis &&
        holding.price === newHolding.price &&
        holding.averageBuyingPrice === newHolding.averageBuyingPrice
      );
    });
  };

  public shouldWeFetchWatchlist = (): boolean => {
    const ONE_MIN = 60 * 1000; /* ms */
    const lastCalled = this.date?.getTime() ?? 0; // use this once all watchlist are grabbed from manager
    if (this.watchlists === undefined || Date.now() - lastCalled > ONE_MIN) {
      return true;
    }
    return false;
  };

  public getWatchlists = (): Watchlist[] => {
    return this.watchlists?.sort((a, b) => (a.name > b.name ? 1 : -1)) ?? [];
  };

  // returns true if update was committed
  public updateWatchlists = (watchlists: Watchlist[]): boolean => {
    this.date = new Date();
    if (this.watchlists && WatchlistStore.compareWatchlists(this.watchlists, watchlists)) {
      return false;
    } else {
      this.watchlists = watchlists;
      this.dispatch({
        type: 'watchlist:updated_watchlists',
        watchlists,
      });
      // TODO: only fire when watchlist changes
      watchlists.forEach(watchlist => {
        this.dispatch({
          type: 'watchlist:updated_watchlist',
          watchlist,
        });
      });
      return true;
    }
  };

  public addWatchlist = (watchlist: Watchlist): void => {
    if (this.watchlists) {
      this.watchlists.push(watchlist);
    } else {
      this.watchlists = [watchlist];
    }
    this.dispatch({
      type: 'watchlist:created_watchlist',
      watchlist: watchlist,
    });
    this.dispatch({
      type: 'watchlist:updated_watchlists',
      watchlists: this.getWatchlistsForce(),
    });
  };

  public updateWatchlist = (watchlist: Watchlist): void => {
    const watchlists = this.watchlists?.map(item => (item.watchlistId === watchlist.watchlistId ? watchlist : item));
    if (this.watchlists && watchlists && WatchlistStore.compareWatchlists(this.watchlists, watchlists)) {
      this.watchlists = watchlists;
      this.dispatch({
        type: 'watchlist:updated_watchlist',
        watchlist: watchlist,
      });
      this.dispatch({
        type: 'watchlist:updated_watchlists',
        watchlists: this.getWatchlistsForce(),
      });
    }
  };

  public removeWatchlist = (watchlist: Watchlist): void => {
    this.watchlists = this.watchlists?.filter(item => item.watchlistId !== watchlist.watchlistId);
    this.dispatch({
      type: 'watchlist:removed_watchlist',
      watchlist: watchlist,
    });
    this.dispatch({
      type: 'watchlist:updated_watchlists',
      watchlists: this.getWatchlistsForce(),
    });
  };

  public updateWatchlistTicker = (watchlistSymbol: WatchlistSymbol): void => {
    if (!this.watchlists) return;

    const nextWatchlists = this.watchlists.map(watchlist => {
      const symbolIndex = watchlist.symbols.findIndex(symbol => symbol.symbolId === watchlistSymbol.symbolId);
      if (symbolIndex === -1) return watchlist;

      const symbols = [...watchlist.symbols];
      symbols[symbolIndex] = watchlistSymbol;
      const nextWatchlist = { ...watchlist, symbols };
      this.dispatch({
        type: 'watchlist:updated_watchlist',
        watchlist: nextWatchlist,
      });
      return nextWatchlist;
    });

    this.watchlists = nextWatchlists;
    this.dispatch({
      type: 'watchlist:updated_watchlists',
      watchlists: this.getWatchlistsForce(),
    });
  };

  public addTickersToWatchlist = (watchlist: Watchlist, tickers: WatchlistSymbol[]): Watchlist => {
    const index = this.watchlists?.findIndex(item => item.watchlistId === watchlist.watchlistId);
    if (index !== undefined && this.watchlists && index >= 0) {
      const watchlist = { ...this.watchlists[index], symbols: [...this.watchlists[index].symbols, ...tickers] };
      this.watchlists[index] = watchlist;
      this.dispatch({
        symbols: tickers.map(t => t.symbol),
        type: 'watchlist:added_ticker_to_watchlist',
        watchlist: watchlist,
      });
      this.dispatch({
        type: 'watchlist:updated_watchlists',
        watchlists: this.getWatchlistsForce(),
      });
      return watchlist;
    }
    return watchlist;
  };

  public removeTickersToWatchlist = (watchlist: Watchlist, tickers: WatchlistSymbol[]): Watchlist => {
    const index = this.watchlists?.findIndex(item => item.watchlistId === watchlist.watchlistId);
    if (index !== undefined && this.watchlists && index >= 0) {
      const watchlist = {
        ...this.watchlists[index],
        symbols: this.watchlists[index].symbols.filter(
          symbol => !tickers.some(ticker => ticker.symbolId === symbol.symbolId),
        ),
      };
      const removedTickers = this.watchlists[index].symbols.filter(symbol =>
        tickers.some(ticker => ticker.symbolId === symbol.symbolId),
      );
      this.watchlists[index] = watchlist;

      this.dispatch({
        symbols: removedTickers.map(wlSymbol => wlSymbol.symbol),
        type: 'watchlist:removed_ticker_from_watchlist',
        watchlist: watchlist,
      });
      this.dispatch({
        type: 'watchlist:updated_watchlists',
        watchlists: this.getWatchlistsForce(),
      });
      return watchlist;
    }
    return watchlist;
  };

  public getWatchlistsForce = (): Watchlist[] => {
    return this.watchlists ? [...this.watchlists] : [];
  };

  public clearWatchlist = (): void => {
    this.watchlists = undefined;
    this.date = undefined;
    this.dispatch({
      type: 'watchlist:updated_watchlists',
      watchlists: [],
    });
  };
}
